import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import FolderTree from './components/FolderTree';
import DocumentGrid from './components/DocumentGrid';
import UploadZone from './components/UploadZone';
import SearchFilters from './components/SearchFilters';
import DocumentPreview from './components/DocumentPreview';

const DocumentManager = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'preview'
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: 'all',
    size: 'all',
    status: 'all'
  });

  // Initialize empty data structures
  const folderStructure = [];
  const documentsData = [];

  // Filter documents based on selected folder, search, and filters
  const getFilteredDocuments = () => {
    let filtered = documentsData;

    // Filter by selected folder
    if (selectedFolder) {
      filtered = filtered?.filter(doc => doc?.folderId === selectedFolder?.id);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered?.filter(doc =>
        doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        doc?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        doc?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      );
    }

    // Filter by type
    if (filters?.type !== 'all') {
      filtered = filtered?.filter(doc => doc?.type === filters?.type);
    }

    // Filter by status
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(doc => doc?.status === filters?.status);
    }

    // Filter by date range
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      let cutoffDate = new Date();

      switch (filters?.dateRange) {
        case 'today':
          cutoffDate = new Date(now?.getFullYear(), now?.getMonth(), now?.getDate());
          break;
        case 'week':
          cutoffDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now?.getFullYear(), now?.getMonth(), 1);
          break;
        case 'year':
          cutoffDate = new Date(now?.getFullYear(), 0, 1);
          break;
        default:
          break;
      }

      if (filters?.dateRange !== 'all') {
        filtered = filtered?.filter(doc => new Date(doc?.dateModified) >= cutoffDate);
      }
    }

    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setSelectedDocument(null);
  };

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  const handleDocumentAction = (action, document) => {
    console.log('Document action:', action, document);
    
    switch (action) {
      case 'view':
        setSelectedDocument(document);
        setViewMode('preview');
        break;
      case 'download':
        console.log('Downloading:', document?.name);
        // Implement actual download functionality here
        break;
      case 'edit': console.log('Editing:', document?.name);
        // Implement edit functionality here
        break;
      case 'share': console.log('Sharing:', document?.name);
        // Implement share functionality here
        break;
      case 'move': console.log('Moving:', document?.name);
        // Implement move functionality here
        break;
      case 'delete':
        console.log('Deleting:', document?.name);
        // Implement delete functionality here
        break;
      case 'duplicate': console.log('Duplicating:', document?.name);
        // Implement duplicate functionality here
        break;
      case 'tags': console.log('Managing tags for:', document?.name);
        // Implement tag management here
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleUpload = (files) => {
    console.log('Uploading files:', files);
    // Implement actual file upload functionality here
    setShowUploadZone(false);
  };

  const handleBulkAction = (action, documents) => {
    console.log('Bulk action:', action, documents);
    // Implement bulk actions here
  };

  const handleExportData = () => {
    console.log('Exporting document data');
    // Implement export functionality here
  };

  const handleImportData = () => {
    console.log('Opening import dialog');
    // Implement import functionality here
  };

  const getStorageStats = () => {
    const totalSize = documentsData?.reduce((sum, doc) => {
      const sizeNum = parseFloat(doc?.size?.replace(/[^\d.]/g, ''));
      const unit = doc?.size?.toLowerCase();
      const multiplier = unit?.includes('gb') ? 1024 : unit?.includes('kb') ? 0.001 : 1;
      return sum + (sizeNum * multiplier);
    }, 0);

    return {
      totalDocuments: documentsData?.length,
      totalSize: totalSize?.toFixed(2) + ' MB',
      usedPercentage: Math?.min((totalSize / 1000) * 100, 100)?.toFixed(1)
    };
  };

  const storageStats = getStorageStats();

  return (
    <>
      <Helmet>
        <title>Document Manager - AccounTech AI</title>
        <meta name="description" content="Comprehensive document management with smart organization and AI-powered insights" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={isSidebarCollapsed} 
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
        />
        
        <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Document Manager</h1>
                  <p className="text-muted-foreground mt-2">
                    Organize, search, and manage your business documents with AI-powered insights
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {/* Storage Stats */}
                  <div className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-lg">
                    <Icon name="HardDrive" size={16} />
                    <span>{storageStats?.totalDocuments} files</span>
                    <span>•</span>
                    <span>{storageStats?.totalSize} used</span>
                  </div>
                  
                  {/* Action Buttons */}
                  <Button
                    variant="outline"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => setShowUploadZone(true)}
                  >
                    Upload Files
                  </Button>
                  
                  <div className="flex items-center bg-muted rounded-lg p-1">
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      iconName="List"
                      onClick={() => setViewMode('list')}
                      title="List View"
                    />
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      iconName="Grid3X3"
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    />
                    {selectedDocument && (
                      <Button
                        variant={viewMode === 'preview' ? 'default' : 'ghost'}
                        size="sm"
                        iconName="Eye"
                        onClick={() => setViewMode('preview')}
                        title="Preview"
                      />
                    )}
                  </div>
                  
                  {/* Additional Actions Dropdown */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      iconName="MoreHorizontal"
                      className="relative"
                      onClick={(e) => {
                        const menu = e?.currentTarget?.nextElementSibling;
                        menu?.classList?.toggle('hidden');
                      }}
                    />
                    <div className="hidden absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-50">
                      <div className="py-1">
                        <button 
                          className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center space-x-2"
                          onClick={handleExportData}
                        >
                          <Icon name="Download" size={16} />
                          <span>Export Data</span>
                        </button>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center space-x-2"
                          onClick={handleImportData}
                        >
                          <Icon name="Upload" size={16} />
                          <span>Import Data</span>
                        </button>
                        <hr className="my-1 border-border" />
                        <button 
                          className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center space-x-2"
                          onClick={() => console.log('Opening settings')}
                        >
                          <Icon name="Settings" size={16} />
                          <span>Settings</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mb-6">
              <SearchFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={filters}
                onFiltersChange={setFilters}
                totalResults={filteredDocuments?.length}
                onSearch={() => {}}
                onFilter={() => {}}
              />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Folder Tree - Left Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="FolderOpen" size={20} className="mr-2" />
                    Folders
                  </h3>
                  <FolderTree
                    folders={folderStructure}
                    selectedFolder={selectedFolder}
                    onFolderSelect={handleFolderSelect}
                    onCreateFolder={() => {}}
                  />
                </div>

                {/* Storage Usage */}
                <div className="bg-card border border-border rounded-lg p-4 mt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="PieChart" size={20} className="mr-2" />
                    Storage Usage
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Used</span>
                      <span className="font-medium">{storageStats?.totalSize}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary rounded-full h-2 transition-all duration-300"
                        style={{ width: `${storageStats?.usedPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{storageStats?.usedPercentage}% used</span>
                      <span>1 GB limit</span>
                    </div>
                    
                    <div className="pt-3 border-t border-border space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Files</span>
                        <span className="font-medium">{storageStats?.totalDocuments}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Folders</span>
                        <span className="font-medium">{folderStructure?.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Grid/List - Main Content */}
              <div className={`${viewMode === 'preview' && selectedDocument ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                <div className="bg-card border border-border rounded-lg">
                  {/* Content Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {selectedFolder ? selectedFolder?.name : 'All Documents'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {filteredDocuments?.length} {filteredDocuments?.length === 1 ? 'document' : 'documents'}
                        </p>
                      </div>
                      
                      {/* Bulk Actions */}
                      {filteredDocuments?.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            onClick={() => handleBulkAction('download', filteredDocuments)}
                          >
                            Download All
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Tag"
                            onClick={() => handleBulkAction('tag', filteredDocuments)}
                          >
                            Bulk Tag
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Document Grid/List */}
                  <div className="p-4">
                    <DocumentGrid
                      documents={filteredDocuments}
                      viewMode={viewMode}
                      selectedDocument={selectedDocument}
                      selectedDocuments={[]}
                      onDocumentSelect={handleDocumentSelect}
                      onDocumentPreview={handleDocumentSelect}
                      onDocumentAction={handleDocumentAction}
                      onDocumentDelete={(doc) => handleDocumentAction('delete', doc)}
                      onBulkAction={handleBulkAction}
                      sortBy="name"
                      sortOrder="asc"
                      onSort={() => {}}
                    />
                  </div>
                </div>
              </div>

              {/* Document Preview - Right Sidebar */}
              {viewMode === 'preview' && selectedDocument && (
                <div className="lg:col-span-1">
                  <div className="sticky top-20">
                    <DocumentPreview
                      document={selectedDocument}
                      onClose={() => {
                        setSelectedDocument(null);
                        setViewMode('grid');
                      }}
                      onAction={handleDocumentAction}
                      onUpdate={() => {}}
                      onDelete={(doc) => handleDocumentAction('delete', doc)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Upload Zone Modal */}
            {showUploadZone && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-card rounded-lg w-full max-w-2xl max-h-[80vh] overflow-auto">
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-foreground flex items-center">
                        <Icon name="Upload" size={20} className="mr-2" />
                        Upload Documents
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        onClick={() => setShowUploadZone(false)}
                      />
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <UploadZone
                      onUpload={handleUpload}
                      onClose={() => setShowUploadZone(false)}
                      selectedFolder={selectedFolder}
                      acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.xlsx', '.xls']}
                      maxFileSize="10MB"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default DocumentManager;